// Cálculos da aba Geomarketing. Funções puras, fora do componente: são a parte que tem regra de
// negócio de verdade (penetração, faixas de distância, recorte por ticket) e a que mais se
// entende lendo isolada.

import type { CustomerLocation, GeoBucket } from '../../modules/eventsApi/geo/customer-location.entity'

export type Coordenada = { lat: number; lng: number }

// Resumo das vendas do período por cliente, montado a partir do pos-api. Uma janela só: quem
// aparece no mapa, quanto gastou e há quanto tempo comprou saem todos daqui.
export type CompraDoCliente = {
    total: number
    vendas: number
    ultimaCompra: Date
    servicos: Set<string>
    usuarios: Set<string>
}

// Resumo dos agendamentos do período por cliente. Enriquece o ponto sem mudar quem entra no
// mapa: a população continua vindo das vendas.
export type AgendaDoCliente = {
    atendimentos: number
    faltas: number
    ultimoAtendimento: Date
}

export type PontoEnriquecido = CustomerLocation & {
    gasto: number
    vendas: number
    // última vez que a pessoa esteve na empresa, por compra OU por atendimento. Quem tem
    // pacote compra uma vez e volta dez: olhar só a venda pintaria essa pessoa como fria.
    diasDesdeUltimaVisita: number
    atendimentos: number
    faltas: number
    // faltas ÷ (atendimentos + faltas). NaN quando não houve agendamento no período — e NaN
    // aqui é melhor que 0, porque 0 significaria "nunca falta".
    taxaDeFalta: number
    distanciaKm: number
}

// Abaixo disso, a média de um bairro é anedota: um cliente com uma venda grande faria o
// bairro liderar o ranking. Vale para ticket médio e para penetração.
export const MINIMO_DE_COMPRADORES_POR_BAIRRO = 3

// Faixas de distância a partir da unidade. A última é aberta.
export const FAIXAS_DE_DISTANCIA: { ate: number; label: string }[] = [
    { ate: 2, label: 'até 2 km' },
    { ate: 5, label: '2 a 5 km' },
    { ate: 10, label: '5 a 10 km' },
    { ate: 25, label: '10 a 25 km' },
    { ate: Infinity, label: 'acima de 25 km' },
]

const RAIO_DA_TERRA_KM = 6371

export function distanciaKm(a: Coordenada, b: Coordenada): number {
    const rad = (x: number) => (x * Math.PI) / 180
    const dLat = rad(b.lat - a.lat)
    const dLng = rad(b.lng - a.lng)
    const h =
        Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2
    return 2 * RAIO_DA_TERRA_KM * Math.asin(Math.sqrt(h))
}

// Polígono que aproxima um círculo sobre a superfície, para desenhar os anéis de distância.
// O maplibre não tem "círculo em metros" como geometria — só raio em pixels, que muda com o
// zoom e não serviria para representar distância real.
export function circuloGeoJson(centro: Coordenada, raioKm: number, lados = 96) {
    const coordenadas: [number, number][] = []
    const latRad = (centro.lat * Math.PI) / 180
    const grausLat = raioKm / 110.574
    const grausLng = raioKm / (111.32 * Math.cos(latRad))
    for (let i = 0; i <= lados; i++) {
        const angulo = (i / lados) * 2 * Math.PI
        coordenadas.push([centro.lng + grausLng * Math.cos(angulo), centro.lat + grausLat * Math.sin(angulo)])
    }
    return {
        type: 'Feature',
        properties: { raioKm },
        geometry: { type: 'Polygon', coordinates: [coordenadas] },
    }
}

export function diasEntre(de: Date, ate: Date): number {
    return Math.floor((ate.getTime() - new Date(de).getTime()) / 86400000)
}

// ---------------------------------------------------------------------------
// Agregações
// ---------------------------------------------------------------------------

export type LinhaDeBairro = {
    bairro: string
    clientes: number
    enderecos: number
    penetracao: number
    gasto: number
    ticketMedio: number
}

// Penetração = COMPRADORES do bairro ÷ endereços que o IBGE contou no bairro inteiro.
// Responde "quanto deste bairro ainda não compra de mim". O numerador é comprador e não
// cadastro de propósito: "2% do bairro compra de mim" é uma afirmação; "2% está na minha
// lista" não é.
export function penetracaoPorBairro(pontos: PontoEnriquecido[], bairros: GeoBucket[]): LinhaDeBairro[] {
    const porBairro = new Map<string, { clientes: number; gasto: number }>()
    for (const ponto of pontos) {
        if (!ponto.neighborhood) continue
        const atual = porBairro.get(ponto.neighborhood) || { clientes: 0, gasto: 0 }
        atual.clientes++
        atual.gasto += ponto.gasto
        porBairro.set(ponto.neighborhood, atual)
    }

    const enderecosPorBairro = new Map(bairros.filter((b) => b.addresses).map((b) => [b.label, b.addresses]))

    const linhas: LinhaDeBairro[] = []
    for (const [bairro, dados] of porBairro) {
        const enderecos = enderecosPorBairro.get(bairro)
        if (!enderecos || dados.clientes < MINIMO_DE_COMPRADORES_POR_BAIRRO) continue
        linhas.push({
            bairro,
            clientes: dados.clientes,
            enderecos,
            penetracao: (dados.clientes / enderecos) * 100,
            gasto: dados.gasto,
            ticketMedio: dados.clientes ? dados.gasto / dados.clientes : 0,
        })
    }
    return linhas.sort((a, b) => b.penetracao - a.penetracao)
}

export type LinhaDeDistancia = {
    label: string
    // toda a população do mapa é compradora do período, então não há distinção a fazer aqui
    compradores: number
    vendas: number
    faturamento: number
    // faturamento ÷ VENDAS. É a definição de ticket médio usada no resto do produto
    // (dashboard-conversions: `ticket: valor / qtde`), então os números batem entre as abas.
    ticketMedio: number
    // faturamento ÷ COMPRADORES. Outra pergunta: quanto cada cliente deixa no período,
    // somando as visitas dele. Sempre >= ticket médio.
    gastoPorComprador: number
    percentualDeCompradores: number
    // agendamentos do período dos clientes desta faixa
    atendimentos: number
    faltas: number
    // percentual de faltas da faixa. É a leitura que o PDV sozinho não dá: quem falta não
    // gera venda, então não existiria nesta tela.
    taxaDeFalta: number
}

// Volume e valor por faixa de distância da unidade. É o embasamento da pergunta "vale abrir
// uma segunda unidade, e onde".
//
// Dois denominadores diferentes e explícitos: ticket médio divide por VENDAS (a definição do
// resto do produto) e gasto por cliente divide por PESSOAS. Confundi-los foi o que fez o
// número sair fora do esperado antes.
export function porDistancia(pontos: PontoEnriquecido[]): LinhaDeDistancia[] {
    const comDistancia = pontos.filter((p) => Number.isFinite(p.distanciaKm))
    if (!comDistancia.length) return []

    const acumulado = FAIXAS_DE_DISTANCIA.map((faixa) => ({
        faixa,
        compradores: 0,
        vendas: 0,
        faturamento: 0,
        atendimentos: 0,
        faltas: 0,
    }))

    for (const ponto of comDistancia) {
        const indice = FAIXAS_DE_DISTANCIA.findIndex((f) => ponto.distanciaKm <= f.ate)
        const alvo = acumulado[indice === -1 ? acumulado.length - 1 : indice]
        alvo.compradores++
        alvo.vendas += ponto.vendas
        alvo.faturamento += ponto.gasto
        alvo.atendimentos += ponto.atendimentos
        alvo.faltas += ponto.faltas
    }

    return acumulado.map((linha) => ({
        label: linha.faixa.label,
        compradores: linha.compradores,
        vendas: linha.vendas,
        faturamento: linha.faturamento,
        ticketMedio: linha.vendas ? linha.faturamento / linha.vendas : 0,
        gastoPorComprador: linha.compradores ? linha.faturamento / linha.compradores : 0,
        percentualDeCompradores: (linha.compradores / comDistancia.length) * 100,
        atendimentos: linha.atendimentos,
        faltas: linha.faltas,
        taxaDeFalta: linha.atendimentos + linha.faltas ? (linha.faltas / (linha.atendimentos + linha.faltas)) * 100 : 0,
    }))
}

export type OrigemPorBairro = {
    bairros: string[]
    origens: { label: string; valores: number[] }[]
}

// Quais canais de aquisição trazem gente de cada bairro. Empilhado, porque o que interessa é
// a composição de cada bairro, não o total.
export function origemPorBairro(
    pontos: PontoEnriquecido[],
    nomeDaOrigem: (referralId: string) => string,
    topBairros = 8,
): OrigemPorBairro {
    const contagem = new Map<string, Map<string, number>>()
    const totalPorBairro = new Map<string, number>()

    for (const ponto of pontos) {
        if (!ponto.neighborhood) continue
        const origem = nomeDaOrigem(ponto.referralId)
        if (!contagem.has(ponto.neighborhood)) contagem.set(ponto.neighborhood, new Map())
        const doBairro = contagem.get(ponto.neighborhood)
        doBairro.set(origem, (doBairro.get(origem) || 0) + 1)
        totalPorBairro.set(ponto.neighborhood, (totalPorBairro.get(ponto.neighborhood) || 0) + 1)
    }

    const bairros = [...totalPorBairro.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, topBairros)
        .map(([bairro]) => bairro)

    const nomesDeOrigem = new Set<string>()
    for (const bairro of bairros) for (const origem of contagem.get(bairro).keys()) nomesDeOrigem.add(origem)

    const origens = [...nomesDeOrigem].map((label) => ({
        label,
        valores: bairros.map((bairro) => contagem.get(bairro).get(label) || 0),
    }))

    return { bairros, origens }
}

// ---------------------------------------------------------------------------
// Cores
// ---------------------------------------------------------------------------

// Duas paletas, e a diferença importa: usar a rampa numa dimensão categórica sugere uma
// ordem que não existe ("origem Instagram é mais que origem indicação"?).
//
// Ordenada: quente = recente/alto, frio = antigo/baixo. Para recência e faixa de gasto.
const PALETA_ORDENADA = ['#D0483E', '#FEB019', '#379E75', '#008FFB', '#6E7B8B']
// Categórica: cores sem hierarquia entre si.
const PALETA_CATEGORICA = ['#008FFB', '#379E75', '#FEB019', '#775DD0', '#6179F3', '#D0483E', '#E07B39']
const COR_SEM_VALOR = '#6E7B8B'

// Acima disso o olho não separa mais as cores num ponto de 7px — o excedente vira "Outras".
const MAXIMO_DE_CATEGORIAS = 7

export type DimensaoDeCor = 'nenhuma' | 'recencia' | 'relacao' | 'falta' | 'origem' | 'gasto' | 'genero'

// Cor única quando o usuário escolhe não colorir. Viva, e não cinza: cinza num mapa lê-se
// como "desativado" ou "sem dado", e aqui o ponto é justamente destacar todos por igual.
const COR_UNICA = '#008FFB'

export type FaixaDeCor = { chave: string; cor: string; rotulo: string }

export type EsquemaDeCor = {
    titulo: string
    // chave de cor de cada ponto; vai para a feature e alimenta o `match` do maplibre
    chaveDoPonto: (ponto: PontoEnriquecido) => string
    faixas: FaixaDeCor[]
}

export const DIMENSOES_DE_COR: { value: DimensaoDeCor; label: string }[] = [
    { value: 'recencia', label: 'Última visita' },
    { value: 'relacao', label: 'Frequência de compra' },
    { value: 'falta', label: 'Faltas na agenda' },
    { value: 'origem', label: 'Origem do cliente' },
    { value: 'gasto', label: 'Faixa de gasto' },
    { value: 'genero', label: 'Gênero' },
    { value: 'nenhuma', label: 'Não colorir' },
]

// Cortes de recência, em dias. Encaixam no período padrão de 12 meses: mês, trimestre,
// semestre, ano. Num recorte curto as faixas do fim ficam vazias — é consequência da escolha
// de período do usuário, e visível.
const CORTES_DE_RECENCIA = [30, 90, 180, 365]

// Mesmos cortes de valor usados no raio do alfinete, para tamanho e cor contarem a mesma história.
const CORTES_DE_GASTO = [200, 1000, 5000]

export function esquemaDeCor(
    dimensao: DimensaoDeCor,
    pontos: PontoEnriquecido[],
    nomeDaOrigem: (referralId: string) => string,
): EsquemaDeCor {
    if (dimensao === 'nenhuma') {
        // faixas vazias: a legenda some e a pintura vira cor única
        return { titulo: '', chaveDoPonto: () => 'todos', faixas: [] }
    }

    if (dimensao === 'falta') {
        return {
            titulo: 'Cor = faltas na agenda',
            chaveDoPonto: (p) => {
                if (!Number.isFinite(p.taxaDeFalta)) return 'sem-agenda'
                if (p.taxaDeFalta === 0) return 'f0'
                if (p.taxaDeFalta <= 25) return 'f1'
                if (p.taxaDeFalta <= 50) return 'f2'
                return 'f3'
            },
            faixas: [
                { chave: 'f3', cor: PALETA_ORDENADA[0], rotulo: 'falta em mais da metade' },
                { chave: 'f2', cor: PALETA_ORDENADA[1], rotulo: 'falta em 25% a 50%' },
                { chave: 'f1', cor: PALETA_ORDENADA[2], rotulo: 'falta em até 25%' },
                { chave: 'f0', cor: PALETA_ORDENADA[3], rotulo: 'nunca faltou' },
                // não é lead: é cliente que comprou sem passar pela agenda (balcão, pacote
                // lançado direto no PDV). Merece um balde próprio em vez de virar "nunca faltou".
                { chave: 'sem-agenda', cor: COR_SEM_VALOR, rotulo: 'sem agendamento no período' },
            ],
        }
    }

    if (dimensao === 'relacao') {
        return {
            titulo: 'Cor = relação com a empresa',
            // sem balde de "não comprou": a população da tela é toda compradora do período
            chaveDoPonto: (p) => (p.vendas === 1 ? 'uma' : 'recorrente'),
            faixas: [
                { chave: 'recorrente', cor: PALETA_CATEGORICA[1], rotulo: 'comprou 2 ou mais vezes' },
                { chave: 'uma', cor: PALETA_CATEGORICA[0], rotulo: 'comprou uma vez' },
            ],
        }
    }

    if (dimensao === 'gasto') {
        return {
            titulo: 'Cor = quanto gastou no período',
            chaveDoPonto: (p) => {
                const indice = CORTES_DE_GASTO.findIndex((corte) => p.gasto <= corte)
                return 'g' + (indice === -1 ? CORTES_DE_GASTO.length : indice)
            },
            faixas: [
                { chave: 'g3', cor: PALETA_ORDENADA[0], rotulo: 'acima de 5 mil' },
                { chave: 'g2', cor: PALETA_ORDENADA[1], rotulo: '1 mil a 5 mil' },
                { chave: 'g1', cor: PALETA_ORDENADA[2], rotulo: '200 a 1 mil' },
                { chave: 'g0', cor: PALETA_ORDENADA[3], rotulo: 'até 200' },
            ],
        }
    }

    if (dimensao === 'genero') {
        return {
            titulo: 'Cor = gênero',
            chaveDoPonto: (p) => p.gender || 'vazio',
            faixas: [
                { chave: 'female', cor: PALETA_CATEGORICA[3], rotulo: 'feminino' },
                { chave: 'male', cor: PALETA_CATEGORICA[0], rotulo: 'masculino' },
                { chave: 'other', cor: PALETA_CATEGORICA[2], rotulo: 'outro' },
                { chave: 'vazio', cor: COR_SEM_VALOR, rotulo: 'não informado' },
            ],
        }
    }

    if (dimensao === 'origem') {
        // Origem é a única dimensão categórica de cardinalidade aberta aqui, então as menos
        // frequentes são agrupadas em "Outras" em vez de virarem cores indistinguíveis.
        const contagem = new Map<string, number>()
        for (const ponto of pontos) {
            const nome = nomeDaOrigem(ponto.referralId)
            contagem.set(nome, (contagem.get(nome) || 0) + 1)
        }
        const principais = [...contagem.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, MAXIMO_DE_CATEGORIAS)
            .map(([nome]) => nome)
        const conhecidas = new Set(principais)

        const faixas: FaixaDeCor[] = principais.map((nome, i) => ({
            chave: nome,
            cor: PALETA_CATEGORICA[i % PALETA_CATEGORICA.length],
            rotulo: nome,
        }))
        if (contagem.size > principais.length) {
            faixas.push({ chave: '__outras__', cor: COR_SEM_VALOR, rotulo: 'outras origens' })
        }

        return {
            titulo: 'Cor = origem do cliente',
            chaveDoPonto: (p) => {
                const nome = nomeDaOrigem(p.referralId)
                return conhecidas.has(nome) ? nome : '__outras__'
            },
            faixas,
        }
    }

    // recência (padrão)
    return {
        titulo: 'Cor = última visita',
        chaveDoPonto: (p) => {
            const indice = CORTES_DE_RECENCIA.findIndex((corte) => p.diasDesdeUltimaVisita <= corte)
            return 'r' + (indice === -1 ? CORTES_DE_RECENCIA.length : indice)
        },
        faixas: [
            { chave: 'r0', cor: PALETA_ORDENADA[0], rotulo: 'último mês' },
            { chave: 'r1', cor: PALETA_ORDENADA[1], rotulo: 'último trimestre' },
            { chave: 'r2', cor: PALETA_ORDENADA[2], rotulo: 'último semestre' },
            { chave: 'r3', cor: PALETA_ORDENADA[3], rotulo: 'último ano' },
            { chave: 'r4', cor: COR_SEM_VALOR, rotulo: 'mais de um ano' },
        ],
    }
}

// Expressão de `circle-color` do maplibre a partir do esquema. Um `match` sobre a chave que já
// vai gravada na feature — assim a mesma mecânica serve para todas as dimensões.
//
// Retorno `any` e não `any[]`: o tipo que o maplibre espera é uma tupla fechada por operador, e
// um array montado em tempo de execução nunca satisfaz essa assinatura.
export function expressaoDeCor(esquema: EsquemaDeCor): any {
    // sem faixas ("não colorir") o maplibre recusaria um match sem par algum: devolve a cor fixa
    if (!esquema.faixas.length) return COR_UNICA
    const pares: any[] = []
    for (const faixa of esquema.faixas) pares.push(faixa.chave, faixa.cor)
    return ['match', ['get', 'corChave'], ...pares, COR_SEM_VALOR]
}
